import { useCallback } from 'react';

import { MegaphoneIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useTitle } from 'react-use';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { MODAL_DELETE, MODAL_FORM, MODAL_WARN } from '@/components/organisms/Modals/ModalConductor';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
} from '@/components/organisms/PageSubHeader/PageSubHeader';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import PermissionsWrapper from '@/components/templates/PermissionsWrapper';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { useServerPagination } from '@/components/ui/table/useServerPagination';
import useDesks from '@/hooks/useDesks';
import {
  BroadcastFilterProvider,
  useBroadcastFilter,
} from '@/modules/broadcast/contexts/BroadcastFilterContext';
import { useBroadcasts } from '@/modules/broadcast/hooks/useBroadcasts';
import { BroadcastTable } from '@/modules/broadcast/pages/Broadcasts/table/Table';
import { popModal, pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { Broadcast, LocationState } from '@/shared/types/broadcast';
import { IntegrationType, WhatsAppIntegration } from '@/shared/types/integration';
import { actions as permissionsActions } from '@/shared/types/permissions';
import { EventName, PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { capitalizeFirstLetter } from '@/util/util';

import { broadcastRules } from '../../utils';

interface CreateModalProps {
  name: string;
  channel: string;
}

const BroadcastsContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const slug = useSelector(selectAccountSlug);
  const { apiStatus, toggleSortDirection, sortDirection, textFilter } = useBroadcastFilter();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'broadcasts', permissionsActions.WRITE)
  );
  const { getAllIntegrations, desks } = useDesks();
  const navigate = useNavigate();

  useSegments(PageName.BROADCASTS);

  useTitle(t('pages.broadcasts', { 0: slug ?? '-' }));

  const {
    broadcasts,
    fetchNextPage,
    hasNextPage: serverHasNextPage,
    totalRows,
    deleteBroadcast,
  } = useBroadcasts();

  // Use the server pagination hook to manage pagination state
  const {
    currentPageIndex,
    setCurrentPageIndex,
    goToNextPage,
    hasNextPage,
    currentPageData: currentPageBroadcasts,
  } = useServerPagination(broadcasts, fetchNextPage, {
    dataSelector: (page) => page?.broadcasts || [],
    resetDependencies: [apiStatus, sortDirection, textFilter],
    serverHasNextPage,
  });

  const handleDeleteClick = useCallback(
    async (broadcast_id: string) => {
      const deleteProps = {
        confirm: true,
        onDelete: () =>
          deleteBroadcast(
            { broadcast_id },
            {
              onSuccess: () => {
                dispatch(popModal());
                navigate(`/${slug}/broadcasts`);
              },
            }
          ),
      };
      dispatch(pushModal(MODAL_DELETE, deleteProps));
    },
    [deleteBroadcast, dispatch, navigate, slug]
  );

  // Navigation happens via the row href; the click handler only tracks
  const handleRowClick = useCallback((broadcast: Broadcast) => {
    trackEvent(EventName.ClickCreateBroadcast, { broadcast_id: broadcast.broadcast_id });
  }, []);

  const getRowHref = useCallback(
    (broadcast: Broadcast) => {
      if (broadcast.status === 'sent') {
        return `/${slug}/broadcasts/${broadcast.broadcast_id}/logs`;
      }
      return `/${slug}/broadcasts/${broadcast.broadcast_id}`;
    },
    [slug]
  );

  const handleCreateClick = useCallback(() => {
    trackEvent(EventName.ClickCreateBroadcast);
    getAllIntegrations(IntegrationType.WHATSAPP).then((waIntegrations: WhatsAppIntegration[]) => {
      const options = waIntegrations
        .filter((i) => i.config?.access_token)
        .map((i) => {
          const deskName = desks.find(
            (d) => d.desk_id === i.desk_id && i.config?.access_token
          )?.name;
          return {
            label: `${capitalizeFirstLetter(i.name)} > ${deskName}`,
            value: i.integration_id,
          };
        });

      const createModalProps = {
        primaryButtonText: t('broadcasts.add_broadcast'),
        title: t('broadcasts.add_broadcast'),
        onCreate: async (data: CreateModalProps) => {
          const integration = waIntegrations.find((o) => o.integration_id === data.channel);
          const state: LocationState = {
            name: data.name,
            integration_id: data.channel,
            channel: integration?.type,
            deskId: integration?.desk_id,
          };
          navigate(`/${slug}/broadcasts/draft`, { state });
          dispatch(popModal());
        },
        fields: [
          { fieldName: 'name', fieldValue: '', rules: broadcastRules().name },
          {
            fieldName: 'channel',
            fieldValue: '',
            type: 'select',
            options,
            rules: broadcastRules().channel,
          },
        ],
      };
      if (options.length > 0) {
        dispatch(pushModal(MODAL_FORM, createModalProps));
      } else {
        const warnProps = {
          title: t('broadcasts.add_broadcast'),
          children: (
            <>
              <p className="text-base leading-6 font-semibold">
                {t('broadcasts.not_connected_integrations')}
              </p>
              <span className="mt-4 text-sm leading-5 font-normal">
                {t('broadcasts.no_integrations')}
              </span>
            </>
          ),
          primaryButtonText: t('broadcasts.go_to_integrations'),
          onPrimarySubmit: () => {
            navigate(`/${slug}/integrations`);
            dispatch(popModal());
          },
        };

        dispatch(pushModal(MODAL_WARN, warnProps));
      }
    });
  }, [desks, dispatch, getAllIntegrations, navigate, slug, t]);

  const getTooltipTitle = () => {
    if (!canWrite) {
      return t('broadcasts.no_permission');
    }

    return '';
  };

  return (
    <>
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    <div className="flex h-8 w-8 items-center justify-center">
                      <MegaphoneIcon size={16} className="text-muted-foreground" />
                    </div>
                    <div className="mr-4 ml-2 h-4 w-px bg-border" />
                    <span className="text-sm leading-5 font-normal">
                      {t('main_navigation.broadcast_other')}
                    </span>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
        actions={
          <PageSubHeaderActions>
            <Button
              onClick={handleCreateClick}
              disabled={!canWrite}
              size="sm"
              title={getTooltipTitle()}
            >
              <PlusIcon size={16} />
              {t('common.create')}
            </Button>
          </PageSubHeaderActions>
        }
      />

      <PageContentWrapper readOnly={!canWrite}>
        <ContextualHelp title={t('broadcasts.contextual_title')} name="about_broadcasts">
          {t('broadcasts.contextual_message')}
        </ContextualHelp>
        <BroadcastTable
          data={currentPageBroadcasts}
          fetchNextPage={goToNextPage}
          hasNextPage={hasNextPage}
          currentPageIndex={currentPageIndex}
          setCurrentPageIndex={setCurrentPageIndex}
          rowCount={totalRows}
          sortDirection={sortDirection}
          toggleSortDirection={toggleSortDirection}
          onRowClick={handleRowClick}
          getRowHref={getRowHref}
          onDeleteClick={handleDeleteClick}
          canWrite={canWrite}
        />
      </PageContentWrapper>
    </>
  );
};

export const Broadcasts = () => {
  return (
    <PermissionsWrapper module="broadcasts">
      <BroadcastFilterProvider>
        <BroadcastsContent />
      </BroadcastFilterProvider>
    </PermissionsWrapper>
  );
};
